/**
 * iter87.199.57 — Pro+ per-rivalry to-do pill (label redesign).
 *
 * Prior shape was a 26×26 circular count badge. Operator ratified a
 * pill-shape label on 2026-02-14:
 *   - `count > 0`: pill reads "N to-do" (singular) / "N to-dos" (plural),
 *     amber-heat styling with a corner ping. Tap → radix Popover, each
 *     row deep-links into the right tournament (iter87.394).
 *   - `count === 0`: pill reads "All caught up" with a checkmark,
 *     translucent volt-blue tint (iter87.399, was green). iter87.394
 *     (user-requested) — now itself a button (box-arrow icon) that
 *     opens the rivalry directly, since the standalone row-level
 *     "Open" button was removed (iter87.395).
 *
 * Mobile-first + iOS Safari safe:
 *   - radix Popover Portal-renders outside the card tree so opening
 *     never nudges neighbouring rivalries.
 *   - No `backdrop-filter` — solid backgrounds only.
 */
import * as React from "react";
import { Check, ExternalLink, Loader2 } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";

export default function RivalryTodoBadge({ leagueId, todos, onOpenTodo, onOpenRivalry, busy, closed = false }) {
    const player = (todos && todos.player) || [];
    const admin = (todos && todos.admin) || [];
    const count = player.length + admin.length;
    const [open, setOpen] = React.useState(false);

    // iter87.422 (user-reported) — A closed rivalry you PLAY IN (not
    // admin) has nothing left to do — showing "All caught up" / "N
    // to-do" here was misleading since it implies the rivalry is
    // still active. Short-circuits to a plain "Closed" pill instead,
    // still tappable (closed rivalries stay fully viewable).
    // iter87.435 (user-requested) — Recolored to the red "Closed" pill
    // style (matches the admin-side Closed & Archived badge) and reused
    // here for BOTH a closed rivalry you play in AND a closed rivalry
    // you admin, so the "Closed" pill behaves identically everywhere.
    if (closed) {
        return (
            <button
                type="button"
                onClick={onOpenRivalry}
                disabled={busy}
                data-testid={`rivalry-todo-badge-${leagueId}-closed`}
                className="inline-flex items-center gap-1 h-[26px] px-2.5 rounded-full border border-heat/50 bg-heat/10 text-heat text-[10px] font-black uppercase tracking-wider leading-none select-none transition-transform active:scale-95 disabled:opacity-50"
                aria-label="Closed — tap to view"
                title="Closed — tap to view (read-only)"
            >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" aria-hidden /> : null}
                Closed
                <ExternalLink className="w-3 h-3 ml-0.5" aria-hidden />
            </button>
        );
    }

    // iter87.394 (user-requested) — Zero state is now clickable: tapping
    // it opens the rivalry, same destination as the (now-removed)
    // "Open" button. The box-arrow icon signals that up front instead
    // of leaving a flat, inert-looking pill.
    if (count === 0) {
        return (
            <button
                type="button"
                onClick={onOpenRivalry}
                disabled={busy}
                data-testid={`rivalry-todo-badge-${leagueId}-zero`}
                className="inline-flex items-center gap-1 h-[26px] px-2.5 rounded-full border text-[10px] font-black uppercase tracking-wider leading-none select-none transition-transform active:scale-95 disabled:opacity-50"
                style={{
                    // iter87.406 (user-reported — "hard to see") —
                    // lighter electric blue than the standard `volt`
                    // (#007AFF), which reads too dark/low-contrast on
                    // this near-black card background.
                    background: "rgba(77, 171, 255, 0.14)",
                    borderColor: "rgba(77, 171, 255, 0.6)",
                    color: "#4DABFF",
                }}
                aria-label="All caught up — open rivalry"
                title="All caught up — tap to open rivalry"
            >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" aria-hidden /> : <Check className="w-3 h-3" aria-hidden />}
                All caught up
                <ExternalLink className="w-3 h-3 ml-0.5" aria-hidden />
            </button>
        );
    }

    const label = `${count} to-do${count === 1 ? "" : "s"}`;

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    data-testid={`rivalry-todo-badge-${leagueId}`}
                    aria-label={`${count} item${count === 1 ? "" : "s"} waiting on you`}
                    className="relative inline-flex items-center gap-1 h-[26px] px-2.5 rounded-full border text-[10px] font-black uppercase tracking-wider leading-none transition-all select-none touch-manipulation"
                    style={{
                        background: "rgba(255, 179, 71, 0.12)",
                        borderColor: "rgba(255, 179, 71, 0.5)",
                        color: "#ffb347",
                    }}
                >
                    {label}
                    {/* Corner ping so the pill draws the eye. Two-layer
                        stack: a static dot (always visible) + an expanding
                        halo (`animate-ping`) that fades outward on a loop.
                        `pointer-events-none` so the halo never intercepts
                        the tap target underneath. */}
                    <span
                        aria-hidden
                        data-testid={`rivalry-todo-badge-${leagueId}-ping`}
                        className="absolute -top-0.5 -right-0.5 flex h-2 w-2 pointer-events-none"
                    >
                        <span
                            className="absolute inline-flex h-full w-full rounded-full opacity-70 animate-ping"
                            style={{ background: "#ffb347" }}
                        />
                        <span
                            className="relative inline-flex h-2 w-2 rounded-full"
                            style={{ background: "#ffb347" }}
                        />
                    </span>
                </button>
            </PopoverTrigger>
            <PopoverContent
                data-testid={`rivalry-todo-popover-${leagueId}`}
                align="end"
                side="bottom"
                sideOffset={6}
                collisionPadding={12}
                className="w-[280px] sm:w-[320px] p-0 rounded-lg border border-zinc-700 bg-[#0f0f10] text-white shadow-2xl overflow-hidden flex flex-col max-h-[min(70vh,var(--radix-popover-content-available-height,70vh))]"
            >
                <div className="flex items-center justify-between px-4 pt-4 pb-2 shrink-0">
                    <p className="font-display font-black text-xs uppercase tracking-tight">
                        Things Waiting on You
                    </p>
                    <span className="text-[10px] text-zinc-500 uppercase tracking-wider">
                        {count} item{count === 1 ? "" : "s"}
                    </span>
                </div>

                <div className="overflow-y-auto px-4 pb-4 min-h-0" data-testid={`rivalry-todo-popover-scroll-${leagueId}`}>
                    {player.length > 0 && (
                        <div data-testid={`rivalry-todo-section-player-${leagueId}`}>
                            <p className="text-[10px] font-black text-zinc-400 uppercase tracking-wider mt-1 mb-1">
                                As Player
                            </p>
                            <ul>
                                {player.map((it, i) => (
                                    <TodoRow key={`p-${it.key}-${it.tournament_id || "league"}-${i}`} item={it} kind="player" busy={busy} onOpenTodo={(item, kind) => { setOpen(false); onOpenTodo?.(item, kind); }} />
                                ))}
                            </ul>
                        </div>
                    )}

                    {admin.length > 0 && (
                        <div data-testid={`rivalry-todo-section-admin-${leagueId}`}>
                            <p className="text-[10px] font-black text-zinc-400 uppercase tracking-wider mt-3 mb-1">
                                As Admin
                            </p>
                            <ul>
                                {admin.map((it, i) => (
                                    <TodoRow key={`a-${it.key}-${it.tournament_id || "league"}-${i}`} item={it} kind="admin" busy={busy} onOpenTodo={(item, kind) => { setOpen(false); onOpenTodo?.(item, kind); }} />
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}

function TodoRow({ item, kind, onOpenTodo, busy }) {
    const pipColor = kind === "admin" ? "#7cff6b" : "#ffb347";
    return (
        <li className="border-b border-dashed border-zinc-800 last:border-b-0">
            <button
                type="button"
                onClick={() => onOpenTodo?.(item, kind)}
                disabled={busy}
                data-testid={`rivalry-todo-row-${kind}-${item.key}-${item.tournament_id || "league"}`}
                className="w-full flex items-start gap-2.5 py-1.5 text-left rounded transition-colors hover:bg-white/[0.04] active:bg-white/[0.08] disabled:opacity-50"
            >
                <span
                    aria-hidden
                    className="flex-shrink-0 w-1.5 h-1.5 rounded-full mt-[7px]"
                    style={{ background: pipColor }}
                />
                <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-white leading-tight">
                        {item.label}
                    </p>
                    {item.tournament_name && (
                        <p className="text-[10px] text-zinc-500 mt-0.5 truncate">
                            {item.tournament_name}
                        </p>
                    )}
                </div>
                <ExternalLink className="w-3 h-3 text-zinc-600 shrink-0 mt-[3px]" aria-hidden />
            </button>
        </li>
    );
}
